import {
  Avatar,
  Box,
  Flex,
  Paper,
  Text,
  Title,
  Pill,
  Collapse,
  Button,
  Anchor,
} from "@mantine/core";
import classes from "./index.module.css";
import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/solid";
import { isMobileDevice } from "../../utils/useDeviceDetection";
import { useDisclosure } from "@mantine/hooks";
import { TRANSFER_STATUSES } from "../../hooks/api/useGetCalculationById";
import { getPensionLogo } from "../../utils/pensionLogo";
import {
  determineStatusColor,
  determineStatusIcon,
  determineStatusMessage,
  determineStatusTitle,
} from "./statuses";
import { Link } from "@tanstack/react-router";
import { REPLAY_BLOCK_CLASS } from "../../utils/statsig";
import { formatSekOrUnknown } from "../../utils/formatPension";
import { knownCapital } from "../../utils/capital";
import { SECONDARY_TEXT_COLOR } from "../../constants/colors";
import type { InsurancesListItemProps } from "../../types";

const InsurancesListItem = ({
  insurance,
  isTransfer,
  isSignInPreparation,
  showMenu,
  showCapital,
  avatarSize = "lg",
}: InsurancesListItemProps) => {
  const capital = knownCapital(insurance.insuranceCapital);
  const Logo = getPensionLogo(insurance.logotype);

  const [opened, { toggle }] = useDisclosure(false);
  const isMobile = isMobileDevice();

  return (
    <Paper radius={"lg"} className={classes.insuranceListItemPaper}>
      <Flex className={classes.insuranceListItemContainer}>
        <Flex
          className={classes.insuranceListItemInfo}
          direction="row"
          align="center"
          gap="md"
          style={{ flex: 1 }}
        >
          <Avatar
            size={avatarSize}
            radius={0}
            classNames={{ root: classes.avatar }}
          >
            {Logo && <Logo />}
          </Avatar>

          <Box ta="left">
            <Title order={3} size="h5" mb={showCapital ? 8 : "xs"}>
              {insurance.insuranceCompany}
            </Title>

            <Text
              c={SECONDARY_TEXT_COLOR}
              size="sm"
              className={REPLAY_BLOCK_CLASS}
            >
              {insurance?.owner?.legalName}
            </Text>
            <Text
              c={SECONDARY_TEXT_COLOR}
              size="sm"
              className={REPLAY_BLOCK_CLASS}
            >
              {insurance?.insuranceIdentifier}
            </Text>
            {showCapital && (
              <Text size="sm" fw={700} mt={8} className={REPLAY_BLOCK_CLASS}>
                {formatSekOrUnknown(capital)}
              </Text>
            )}
          </Box>
          {showMenu && !isMobile && (
            /*(
            <Flex direction="column" gap="xs" style={{ marginLeft: "auto" }}>
              <Button component={Link} to={`/${insurance.insuranceIdentifier}/information`} variant="light" size="compact-sm">
                Information
              </Button>
              <Button component={Link} to={`/${insurance.insuranceIdentifier}/transactions`} variant="light" size="compact-sm">
                Transaktioner
              </Button>
            </Flex>
          )*/
            <Flex direction="column" gap="xs" style={{ marginLeft: "auto" }}>
              <Button
                component={Link}
                to={`/${insurance.insuranceIdentifier}/information`}
                variant="light"
                size="compact-sm"
              >
                Information
              </Button>
            </Flex>
          )}
        </Flex>

        {showMenu && isMobile && (
          /* <Flex direction="column" gap="xs" style={{ width: "100%" }}>
            <Button component={Link} to={`/${insurance.insuranceIdentifier}/information`} variant="light" fullWidth>
              Information
            </Button>
            <Button component={Link} to={`/${insurance.insuranceIdentifier}/transactions`} variant="light" fullWidth>
              Transaktioner
            </Button>
            
          </Flex>*/
          <Flex direction="column" gap="xs" style={{ width: "100%" }}>
            <Button
              component={Link}
              to={`/${insurance.insuranceIdentifier}/information`}
              variant="light"
              fullWidth
            >
              Information
            </Button>
          </Flex>
        )}

        {insurance?.transferDocument && !isTransfer && (
          <Anchor
            href={insurance?.transferDocument}
            c="dimmed"
            underline="always"
          >
            Ladda ner flyttblad
            <ArrowDownTrayIcon
              aria-hidden="true"
              style={{ height: "14px", width: "14px", marginLeft: "10px" }}
            />
          </Anchor>
        )}

        {isTransfer && !isSignInPreparation && (
          <Pill
            classNames={{
              root: classes.insuranceListItemPill,
              label: classes.insuranceListItemPillLabel,
            }}
            size="lg"
            bg={determineStatusColor(
              insurance?.statusInternal ?? TRANSFER_STATUSES.NEW,
            )}
          >
            {`${determineStatusTitle(insurance.statusInternal ?? TRANSFER_STATUSES.NEW, insurance?.isDigitalTransfer ?? true)} `}
            <span aria-hidden="true">
              {determineStatusIcon(
                insurance.statusInternal ?? TRANSFER_STATUSES.NEW,
                insurance?.isDigitalTransfer ?? true,
              )}
            </span>
          </Pill>
        )}
      </Flex>

      {isTransfer && (
        <Flex
          direction={"column"}
          bg={"var(--wallet-color-neutrals-gray-100)"}
          className={classes.insuranceListItemDropdown}
        >
          <Button
            onClick={toggle}
            variant="transparent"
            color={SECONDARY_TEXT_COLOR}
            aria-expanded={opened}
          >
            Vad händer nu?{" "}
            {opened ? (
              <ChevronUpIcon
                aria-hidden="true"
                height={"16px"}
                width={"16px"}
              />
            ) : (
              <ChevronDownIcon
                aria-hidden="true"
                height={"16px"}
                width={"16px"}
              />
            )}
          </Button>
          <Collapse in={opened} px={"md"} pb={"sm"}>
            <Text>
              {determineStatusMessage(
                insurance.statusInternal ?? TRANSFER_STATUSES.NEW,
              )}
            </Text>
          </Collapse>
        </Flex>
      )}
    </Paper>
  );
};

export default InsurancesListItem;
